/**
 * Simple App Component
 * Clean routing with simple authentication
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RoutineSetupChecklistProvider, useRoutineSetupChecklist } from './contexts/RoutineSetupChecklistContext';
import { ToastProvider } from './contexts/ToastContext';
import AuthGuard from './components/AuthGuard/AuthGuard';
import Topbar from './components/Topbar/Topbar';
import AmbientBackdrop from './components/AmbientBackdrop/AmbientBackdrop';
import { MoodProvider } from './theme/MoodContext';
import './components/Colors/Colors.css';
import './styles/moods.css';
import './App.css';

// Import your existing pages
import HomePage from './pages/HomePage';
import SkincareCabinet from './pages/SkincareCabinet';
import MorningRoutine from './pages/MorningRoutine';
import NightRoutine from './pages/NightRoutine';
import ProductDetailPage from './pages/ProductDetailPage';
import Onboarding from './pages/Onboarding';
import CustomRoutine from './pages/CustomRoutine';
import About from './pages/About';
import Contact from './pages/Contact';
import DesignSystem from './pages/DesignSystem';
import Settings from './pages/Settings';
import RoutineSetupChecklist from './components/RoutineSetupChecklist/RoutineSetupChecklist';

// Layout: full-bleed mood backdrop + topbar + centered content column
const Layout = ({ children }) => (
  <div className="vertical-container">
    <AmbientBackdrop />
    <Topbar />
    <div className="horizontal-container horizontal-container--no-sidebar">
      <main className="app-main">
        {children}
      </main>
    </div>
    <RoutineSetupChecklistFloating />
  </div>
);

// Routine Setup Checklist (Getting Started) - disappears when dismissed or all steps complete
function RoutineSetupChecklistFloating() {
  const { routineSetupChecklistCompleted, routineSetupChecklistDismissed } = useRoutineSetupChecklist();
  if (routineSetupChecklistCompleted || routineSetupChecklistDismissed) return null;
  return (
    <div className="routine-setup-checklist-floating">
      <RoutineSetupChecklist />
    </div>
  );
}

// Full-page layout for onboarding (no topbar or sidebar)
const FullPageLayout = ({ children }) => (
  <div className="full-page-layout">
    <AmbientBackdrop />
    <div className="full-page-layout-content">
      {children}
    </div>
  </div>
);

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <MoodProvider>
        <RoutineSetupChecklistProvider>
          <ToastProvider>
          <div className="App">
            <Routes>
            {/* Public routes */}
            <Route path="/learn/about" element={<Layout><About /></Layout>} />
            <Route path="/help" element={<Layout><Contact /></Layout>} />
            <Route path="/design-system" element={<Layout><DesignSystem /></Layout>} />
            
            {/* Public home page - AuthGuard with requireAuth={false} allows guests and redirects logged-in users to onboarding if needed */}
            <Route path="/" element={
              <AuthGuard requireAuth={false}>
                <Layout>
                  <HomePage />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Public routes - accessible without authentication */}
            <Route path="/skincarecabinet" element={
              <Layout>
                <SkincareCabinet />
              </Layout>
            } />
            
            <Route path="/routines/morning" element={
              <Layout>
                <MorningRoutine />
              </Layout>
            } />
            
            <Route path="/routines/night" element={
              <Layout>
                <NightRoutine />
              </Layout>
            } />
            
            <Route path="/product/:id" element={
              <Layout>
                <ProductDetailPage />
              </Layout>
            } />
            
            {/* Onboarding requires authentication */}
            <Route path="/onboarding" element={
              <AuthGuard requireAuth={true}>
                <FullPageLayout>
                  <Onboarding />
                </FullPageLayout>
              </AuthGuard>
            } />
            
            {/* Settings / Profile - requires authentication */}
            <Route path="/settings" element={
              <AuthGuard requireAuth={true}>
                <Layout>
                  <Settings />
                </Layout>
              </AuthGuard>
            } />
            
            <Route path="/routines/custom/:routineType" element={
              <Layout>
                <CustomRoutine />
              </Layout>
            } />
            
            {/* Auth callback route - no guard needed */}
            <Route path="/auth/callback" element={
              <div className="auth-callback-screen">
                <AmbientBackdrop />
                <span className="auth-callback-message">Processing authentication...</span>
              </div>
            } />
            </Routes>
          </div>
          </ToastProvider>
        </RoutineSetupChecklistProvider>
        </MoodProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;
